import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import Caret from "@components/caret/caret";
import {
  CharacterHandle,
  CharacterLottieWeb,
} from "@components/character/character-lottie-web";

import { useAppSelector } from "@hooks/use-store";
import { CHARACTERS } from "@constants/characters.constant";

type PropsType = {
  typedText: string;
  nextLetter: string;
  restText: string;
  currentLetterIndex: number;
  characterRef: React.MutableRefObject<null | CharacterHandle>;
  focusFunction: () => void;
  overlayNode: React.ReactNode;
  isOverlayVisible: boolean;
};

function ExerciseProgressMobile({
  typedText,
  nextLetter,
  restText,
  currentLetterIndex,
  characterRef,
  focusFunction,
  overlayNode,
  isOverlayVisible,
}: PropsType) {
  const { errorKey, activeCharacterName } = useAppSelector(
    (state) => state.app,
  );

  const [left, setLeft] = useState<number>(0);
  const div = useRef<HTMLDivElement>(null);
  const letterRef = useRef<HTMLDivElement>(null);
  const typedTextRef = useRef<HTMLSpanElement>(null);

  const activeCharacter = activeCharacterName
    ? CHARACTERS[activeCharacterName]
    : null;

  const character = activeCharacter ? (
    <CharacterLottieWeb
      key={`${activeCharacterName}`}
      character={activeCharacter}
      ref={characterRef}
      width={100}
    />
  ) : null;

  useEffect(() => {
    if (typedTextRef.current) {
      setLeft(typedTextRef.current.clientWidth);
    }
  }, [currentLetterIndex]);

  const isCursorEnabled = character === null;

  return (
    <div className='relative'>
      {overlayNode}
      <div
        className={classNames(
          "line relative -mx-4 flex py-3 font-source-code text-large-t-xs font-bold leading-[4.3rem]",
          {
            "blur-none": !isOverlayVisible,
            "blur-10": isOverlayVisible,
          },
        )}
        role='presentation'
        onClick={focusFunction}
      >
        {character || (
          <div className='relative z-10 ml-4'>
            <Caret
              caretPosition={{
                x: 0,
                y: 0,
              }}
            />
          </div>
        )}
        <div
          ref={div}
          className={classNames(
            "-mr-4 flex flex-1 items-center overflow-hidden",
            {
              relative: !isCursorEnabled,
            },
          )}
        >
          <div
            className={classNames(
              "transition-left flex duration-300 ease-in-out",
              {
                "bg-key-error": errorKey !== "",
                absolute: !isCursorEnabled,
                relative: isCursorEnabled,
              },
            )}
            style={{
              left: `-${left}px`,
            }}
          >
            <span ref={typedTextRef} className='text-white'>
              {typedText}
            </span>
            <span ref={letterRef} className={classNames("letter")}>
              {nextLetter}
            </span>
            <span>{restText}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExerciseProgressMobile;
